<template>
  <div class="credit-analysis-page">
    <credit-search
      :loading="loading"
      :value="searchedCnpj"
      @input="searchedCnpj = $event"
      @search="handleSearch"
    />

    <template v-if="company">
      <company-result :company="company" />

      <analysis-history
        :analyses="filteredAnalyses"
        :downloading-analysis-id="downloadingAnalysisId"
        @new-analysis="handleNewAnalysis"
        @filter="handleFilter"
        @view="handleView"
        @start-analysis="handleStartAnalysis"
        @download="handleDownload"
        @history="handleHistory"
      />
      <analysis-history-modal
        v-model="showHistoryModal"
        :history="analysisHistory"
      />
    </template>


    <confirm-modal
      :visible="showConfirmModal"
      title="Iniciar pré-análise"
      message="Tem certeza que deseja iniciar a pré-análise?"
      @confirm="handleConfirmPreAnalysis"
      @cancel="handleCancel"
    />

    <confirm-modal
      :visible="showRecentAnalysisConfirmModal"
      title="Como deseja obter os dados?"
      message="Encontramos uma análise recente. Você pode reutilizar os dados salvos ou buscar dados novamente."
      checkbox-label="Buscar dados novamente"
      checkbox-help="Ao ativar, uma nova consulta será realizada e poderá gerar custos adicionais."
      cancel-text="Cancelar"
      confirm-text="Continuar"
      @confirm="handleConfirmNewAnalysis"
      @cancel="handleCancelNewAnalysis"
    />

    <analysis-filter-modal
      v-model="showFilterModal"
      :filters="filters"
      :credit-products="creditProductFilterOptions"
      :status-options="statusOptions"
      :responsible-options="responsibleOptions"
      @apply="applyFilters"
      @clear="clearFilters"
    />

    <new-analysis-modal
      v-model="showNewAnalysisModal"
      :analysis="newAnalysis"
      :credit-products="creditProducts"
      :can-create-analysis="canCreateAnalysis"
      @create="createAnalysis"
    />

  </div>
</template>

<script>
import {
  BModal,
  BButton
} from 'bootstrap-vue'

import CreditSearch from './components/CreditAnalysis/CreditSearch.vue'
import CompanyResult from './components/CreditAnalysis/CompanyResult.vue'
import AnalysisHistory from './components/CreditAnalysis/AnalysisHistory.vue'
import AnalysisFilterModal from './components/CreditAnalysis/AnalysisFilterModal.vue'
import NewAnalysisModal from './components/CreditAnalysis/NewAnalysisModal.vue'
import AnalysisHistoryModal from './components/CreditAnalysis/AnalysisHistoryModal.vue'
import ConfirmModal from './components/ConfirmModal.vue'
import CreditAnalysisService from '@/service/credit-analysis'
import { callLoading } from '@/service/utilities'
import { DateTime } from 'luxon'
import parseMoneyToNumber from '@/utils/parseMoneyToNumber'

export default {
  name: 'CreditAnalysis',

  components: {
    BModal,
    BButton,
    CreditSearch,
    CompanyResult,
    AnalysisHistory,
    AnalysisFilterModal,
    NewAnalysisModal,
    AnalysisHistoryModal,
    ConfirmModal,
  },

  data() {
    return {
      loading: false,
      downloadingAnalysisId: null,
      company: null,
      analysisHistory: [],
      showNotFoundModal: false,
      showConfirmModal: false,
      showRecentAnalysisConfirmModal: false,
      newQuery: false,
      showFilterModal: false,
      showNewAnalysisModal: false,
      showHistoryModal: false,

      searchedCnpj: null,
      creditProducts: [],
      analyses: [],
      filters: {
        proposal: '',
        analysisDate: '',
        approved: '',
        responsible: ''
      },
      newAnalysis: {
        product_id: null,
        capital_social: '',
        annual_revenue: ''
      }
    }
  },

  computed: {
    creditProductFilterOptions() {
      return this.creditProducts.map(product => ({
        value: product.value ? product.text : '',
        text: product.text
      }))
    },

    filteredAnalyses() {
      return this.analyses.filter(analysis => (
        this.matchesSelectFilter(
          analysis.product,
          this.filters.proposal
        ) &&
        this.matchesDateFilter(
          analysis.analysisDate,
          this.filters.analysisDate
        ) &&
        this.matchesSelectFilter(
          analysis.approved,
          this.filters.approved
        ) &&
        this.matchesSelectFilter(
          analysis.responsible,
          this.filters.responsible
        )
      ))
    },

    statusOptions() {
      return [
        {
          value: '',
          text: 'Todos'
        },

        ...this.analyses
          .map(analysis => analysis.approved)
          .filter(
            (status, index, statuses) =>
              status &&
              statuses.indexOf(status) === index
          )
          .map(status => ({
            value: status,
            text: status
          }))
      ]
    },
    responsibleOptions() {
      return [
        {
          value: '',
          text: 'Todos'
        },
        ...this.analyses
          .map(analysis => analysis.responsible)
          .filter(
            (responsible, index, responsibles) =>
              responsible &&
              responsibles.indexOf(responsible) === index
          )
          .map(responsible => ({
            value: responsible,
            text: responsible
          }))
      ]
    },

    canCreateAnalysis() {
      return Boolean(
        this.newAnalysis.product_id &&
        this.newAnalysis.capital_social &&
        this.newAnalysis.annual_revenue
      )
    }
  },

  mounted() {
    this.restoreSearchState()
  },

  beforeRouteLeave(to, from, next) {
    const routesToKeepState = ['pre-analysis', 'params']

    if (!routesToKeepState.includes(to.name)) {
      sessionStorage.removeItem('credit-analysis-search')
    }

    next()
  },


  methods: {
    async handleSearch(cnpj) {
      this.loading = true
      callLoading(true)

      this.company = null
      this.analyses = []
      this.searchedCnpj = cnpj

    try {
      const document = cnpj.replace(/\D/g, '')

      const response =
        await CreditAnalysisService.getClientInformation(document)

      const data = response && response.data
      const result = data && (data.data || data)

      if (response && response.status === 400) {
        this.$toast.error(
          'O CNPJ informado é inválido.'
        )
      } else if (
        response &&
        response.status >= 200 &&
        response.status < 300 &&
        result
      ) {
        this.company = result.company

        this.analyses = Array.isArray(result.analysis)
          ? result.analysis.map(
            analysis => this.normalizeAnalysis(analysis)
          )
          : []

        this.saveSearchState()
      } else {
        this.showNotFoundModal = true
      }
    } catch (error) {
      this.$toast.error('Não foi possível consultar a análise de crédito.')
    } finally {
      this.loading = false
      callLoading(false)
    }
    },

    matchesSelectFilter(value, filter) {
      if (!filter) {
        return true
      }

      return String(value || '').toLowerCase() === String(filter).toLowerCase()
    },


    matchesDateFilter(value, filter) {
      if (!filter) {
        return true
      }

      if (!value) {
        return false
      }

      return DateTime.fromISO(String(value)).toFormat('yyyy-MM-dd') === filter
    },

    async loadProducts() {
      try {
        const response = await CreditAnalysisService.getProducts()
        const data = response && response.data
        const products = data && (data.data || data)

      this.creditProducts = Array.isArray(products)
        ? [
            {
              value: '',
              text: 'Todos'
            },
            ...products.map(product => ({
              value: product.id,
              text: product.name
            }))
          ]
        : []

      return true
      } catch (error) {
        this.$toast.error('Não foi possível carregar os produtos de crédito.')
        return false
      }
    },

    restoreSearchState() {
      const savedState =
        sessionStorage.getItem('credit-analysis-search')

      if (!savedState) {
        return
      }

      try {
        const state = JSON.parse(savedState)
        const cnpj = typeof state === 'string'
          ? state
          : state.searchedCnpj

        if (cnpj) {
          this.searchedCnpj = cnpj
          this.handleSearch(cnpj)
        } else {
          sessionStorage.removeItem('credit-analysis-search')
        }
      } catch (error) {
        sessionStorage.removeItem(
          'credit-analysis-search'
        )
      }
    },

    saveSearchState() {
      sessionStorage.setItem(
        'credit-analysis-search',
        JSON.stringify(this.searchedCnpj)
      )
    },

    normalizeAnalysis(analysis) {
      const approved = analysis.approved
      const currentStep =
        analysis.currentStep || analysis.current_step
      const product = analysis.product
      const productId =
        analysis.product_id ||
        analysis.productId ||
        (
          product &&
          typeof product === 'object' &&
          (product.id || product.value)
        )
      const rawProposal = analysis.proposal || product
      const proposal =
        rawProposal &&
        typeof rawProposal === 'object'
          ? rawProposal.text || rawProposal.name || rawProposal.description
          : rawProposal

      return {
        ...analysis,

        product_id: productId,
        proposal,

        analysisDate:
          analysis.analysisDate ||
          analysis.analysis_date,

        responsible:
          analysis.responsible,

        status:
          analysis.status ||
          (
            typeof approved === 'string'
              ? approved
              : currentStep === 'Finish'
                ? (
                  approved
                    ? 'Aprovada'
                    : 'Reprovada'
                )
                : 'Em análise'
          ),

        statusType:
          analysis.statusType ||
          (
            typeof approved === 'string'
              ? (
                approved === 'Negado'
                  ? 'danger'
                  : 'success'
              )
              : currentStep === 'Finish'
                ? (
                  approved
                    ? 'success'
                    : 'danger'
                )
                : 'warning'
          )
      }
    },

    handleNotFoundCancel() {
      this.showNotFoundModal = false
    },

    handleNewPreAnalysis() {
      this.showNotFoundModal = false
      this.openNewAnalysisModal()
    },

    handleNewAnalysis() {
      const now = DateTime.local()
      const thirtyDaysAgo = now.minus({ days: 30 })
      const hasRecentAnalysis = this.analyses.some(analysis => {
        const analysisDate = DateTime.fromISO(
          String(analysis.analysisDate || '')
        )

        return analysisDate.isValid &&
          analysisDate >= thirtyDaysAgo &&
          analysisDate <= now
      })

      if (hasRecentAnalysis) {
        this.showRecentAnalysisConfirmModal = true
        return
      }

      this.openNewAnalysisModal()
    },

    handleConfirmNewAnalysis(newQuery) {
      this.showRecentAnalysisConfirmModal = false
      this.openNewAnalysisModal(newQuery)
    },

    handleCancelNewAnalysis() {
      this.showRecentAnalysisConfirmModal = false
    },

    async handleConfirmPreAnalysis() {
      this.showConfirmModal = false
      await this.createAnalysis()
    },

    async createAnalysis(analysis = this.newAnalysis) {
    callLoading(true)

    try {
      const payload = {
        product_id: Number(analysis.product_id),
        document: this.searchedCnpj.replace(/\D/g, ''),
        company_name: this.company ? this.company.name : '',
        registration_status: this.company && this.company.registrationStatus,
        foundation_date: this.company && this.company.foundationDate,
        branch_of_activity: this.company && this.company.branchOfActivity,
        location: this.company && this.company.location,
        responsible: this.$store.state.user.data.name,
        social_capital: parseMoneyToNumber(analysis.capital_social),
        annual_revenue: parseMoneyToNumber(analysis.annual_revenue),
        ...(this.newQuery ? { new_query: true } : {})
      }

      const response = await CreditAnalysisService.create(payload)

      if (response && response.status >= 200 && response.status < 300) {
        this.showNewAnalysisModal = false
        this.$toast.success('Pré-análise criada com sucesso.')
        await this.handleSearch(this.searchedCnpj)
      } else {
        this.$toast.error('Não foi possível criar a pré-análise.')
      }
    } catch (error) {
      this.$toast.error('Não foi possível criar a pré-análise.')
    } finally {
      callLoading(false)
      }
    },

    handleCancel() {
      this.showConfirmModal = false
    },

    async handleFilter() {
      const productsLoaded = await this.loadProducts()

      if (!productsLoaded) {
        return
      }

      this.showFilterModal = true
    },

    applyFilters(filters) {
      this.filters = {
        ...filters
      }

      this.showFilterModal = false
    },

    clearFilters() {
      this.filters = {
        proposal: '',
        analysisDate: '',
        approved: '',
        responsible: ''
      }

      this.showFilterModal = false
    },

    async openNewAnalysisModal(newQuery = false) {
      const productsLoaded = await this.loadProducts()

      if (!productsLoaded) {
        return
      }

      this.newQuery = newQuery
      this.newAnalysis = {
        product_id: null,
        capital_social: this.company ? this.company.socialCapital : '',
        annual_revenue: ''
      }
      this.showNewAnalysisModal = true
    },

    matchesFilter(value, filter) {
      if (!filter) {
        return true
      }

      if (Array.isArray(value)) {
        return value.some(item => this.matchesFilter(item, filter))
      }

      const normalizedValue =
        String(value || '').toLowerCase()

      const normalizedFilter =
        String(filter).toLowerCase()

      const formattedDate = value
        ? DateTime.fromISO(String(value)).toFormat('dd/MM/yyyy HH:mm').toLowerCase()
        : ''

      return (
        normalizedValue.includes(normalizedFilter) ||
        formattedDate.includes(normalizedFilter)
      )
    },

    async handleView(analysis) {
      const analysisId = analysis.id || analysis.analysis_id

      if (!analysisId) {
        this.$toast.error('Não foi possível identificar a pré-análise.')
        return
      }

      try {
        await this.$router.push({
          name: 'pre-analysis',
          params: { id: String(analysisId) }
        })
      } catch (error) {
        this.$toast.error('Não foi possível abrir a pré-análise.')
      }
    },

    handleStartAnalysis(analysis) {
      console.log('Iniciar análise', analysis)
    },

  async handleDownload(analysis) {
    try {
      const analysisId = analysis.id || analysis.analysis_id

      if (!analysisId) {
        this.$toast.error(
          'Não foi possível identificar a pré-análise.'
        )
        return
      }

      this.downloadingAnalysisId = analysisId

      const response = await CreditAnalysisService.getDataToPdf(analysisId)

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300
      ) {
        throw new Error('Não foi possível carregar o PDF.')
      }

     const blob = new Blob([response.data], {
        type: 'application/pdf'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url

      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/)

      link.download = filenameMatch
        ? filenameMatch[1]
        : `pre-analise-${analysisId}.pdf`

      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error(error)

      this.$toast.error(
        'Não foi possível baixar o relatório.'
      )
    } finally {
      this.downloadingAnalysisId = null
    }
  },

  async handleHistory(analysis) {
  const analysisId = analysis.id || analysis.analysis_id

  if (!analysisId) {
    this.$toast.error('Não foi possível identificar a pré-análise.')
    return
  }

  callLoading(true)

  try {
    const response = await CreditAnalysisService.getHistoryById(analysisId)

    if (response && response.status >= 200 && response.status < 300) {
      this.analysisHistory = response.data || []
      this.showHistoryModal = true
    } else {
      this.$toast.error('Não foi possível carregar o histórico.')
    }
  } catch (error) {
    this.$toast.error('Não foi possível carregar o histórico.')
  } finally {
    callLoading(false)
  }
}
  }
}
</script>

<style scoped>
.credit-analysis-page {
  padding: 14px 26px 30px;
}

.form-control:focus,
.custom-select:focus {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 2px rgba(185, 183, 211, 0.712);
}
</style>